/**
 * Sprint S13.1.A — Step 8: informed consent + final submit.
 *
 * Calls Phase E `POST /api/onboarding/complete` (idempotent). On success,
 * refreshes auth state, clears the draft and opens the dashboard.
 */
import { useQueryClient } from "@tanstack/react-query";
import { me } from "@/api/auth";
import { useAuthStore } from "@/stores/auth";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { completeOnboarding } from "@/api/onboarding";


export function Step8Submit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const data = useOnboardingStore((s) => s.data);
  const setField = useOnboardingStore((s) => s.setField);
  const setSubmitting = useOnboardingStore((s) => s.setSubmitting);
  const setSubmitError = useOnboardingStore((s) => s.setSubmitError);
  const isSubmitting = useOnboardingStore((s) => s.isSubmitting);
  const submitError = useOnboardingStore((s) => s.submitError);
  const reset = useOnboardingStore((s) => s.reset);

  const [consentChecked, setConsentChecked] = useState(
    data.informed_consent_signed,
  );

  const handleSubmit = async () => {
    const sessionToken = useAuthStore.getState().token;
    setSubmitError(null);

    if (!consentChecked) {
      setSubmitError(t("onboarding.consent.required"));
      return;
    }

    setField("informed_consent_signed", true);
    setSubmitting(true);

    try {
      const finalData = { ...data, informed_consent_signed: true };
      await completeOnboarding(finalData);
      if (useAuthStore.getState().token !== sessionToken) return;
      // Drop any pre-completion /auth/me request before reading authoritative
      // server state. Do not navigate with its five-minute cached null timestamp.
      await queryClient.cancelQueries({ queryKey: ["auth-me"] });
      await queryClient.fetchQuery({ queryKey: ["auth-me"], queryFn: me, staleTime: 0 });
      if (useAuthStore.getState().token !== sessionToken) return;
      await queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] !== "auth-me",
        refetchType: "none",
      });
      reset();
      useOnboardingStore.persist.clearStorage();
      navigate("/", { replace: true });
    } catch (error) {
      if (useAuthStore.getState().token !== sessionToken) return;
      const detail =
        (error as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? t("onboarding.submit.error");
      setSubmitError(typeof detail === "string" ? detail : t("profile_editor.validation_error"));
    } finally {
      if (useAuthStore.getState().token === sessionToken && useOnboardingStore.getState().isSubmitting) setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2
          id="step-heading"
          tabIndex={-1}
          className="mb-2 text-xl font-semibold text-navy-deep sm:text-2xl"
        >
          {t("onboarding.step.submit.title")}
        </h2>
        <p className="text-[13px] text-taupe sm:text-[14px]">
          {t("onboarding.step.submit.subtitle")}
        </p>
      </div>

      <div className="rounded-xl border border-cream-border bg-cream-light p-5">
        <p className="mb-2 font-medium text-navy">
          {t("onboarding.consent.title")}
        </p>
        <p className="text-[13px] leading-relaxed text-taupe">
          {t("onboarding.consent.body")}
        </p>
      </div>

      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={consentChecked}
          onChange={(e) => setConsentChecked(e.target.checked)}
          className="mt-1 h-4 w-4 cursor-pointer accent-amber"
        />
        <span className="text-[14px] text-navy-deep">
          {t("onboarding.consent.checkbox_label")}
        </span>
      </label>

      {submitError ? (
        <div
          role="alert"
          className="rounded-lg border border-coral/30 bg-coral/10 p-3 text-[13px] text-coral"
        >
          {submitError}
        </div>
      ) : null}

      <Button
        type="button"
        onClick={handleSubmit}
        disabled={isSubmitting || !consentChecked}
        className="w-full"
      >
        {isSubmitting
          ? t("onboarding.submit.submitting")
          : t("onboarding.submit.cta")}
      </Button>
    </div>
  );
}
